import React from 'react'
import { NavLink } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import SimpleHeader from '../layouts/SimpleHeader'
import SideMenu from '../layouts/SideMenu'
import MenuWrap from '../components/BurgerMenu/MenuWrap'
import LoadingPage from './LoadingPage'
import { GET_ACCOUNT_SUMMARY_QUERY } from '../services/GraphQLQueries'

const formatDateTime = (value) => {
  if (!value) {
    return '-'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '-'
  }

  return parsed.toLocaleString()
}

const formatMoney = (amountCents, currency = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format((amountCents || 0) / 100)
}

const formatRate = (amountCents, currency = 'usd') => {
  if (!amountCents) {
    return '-'
  }

  return `${formatMoney(amountCents, currency)} / CVE`
}

const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value || 0)
}

const AccountSummary = () => {
  const { data, loading, error } = useQuery(GET_ACCOUNT_SUMMARY_QUERY, {
    fetchPolicy: 'no-cache',
  })

  if (loading) {
    return <LoadingPage />
  }

  const summary = data?.accountSummary
  const users = summary?.users ?? []
  const accountLabel = summary?.organizationName || 'Organization'
  const notVerifiedCves = Math.max((summary?.totalCves || 0) - (summary?.verifiedCves || 0), 0)
  const committedCves = summary?.committedAnnualCves || 0
  const processedCves = summary?.currentPeriodProcessedCves || 0
  const hasAnnualCommitment = committedCves > 0
  const remainingCves =
    summary?.remainingCommittedCves ?? Math.max(committedCves - processedCves, 0)
  const capacityUsedPercent =
    hasAnnualCommitment
      ? Math.min(Math.max((processedCves / committedCves) * 100, 0), 100)
      : 0
  const usageBarPercent = hasAnnualCommitment
    ? capacityUsedPercent
    : processedCves > 0 ? 100 : 0

  return (
    <div id="outer-container" style={{ height: '100%' }}>
      <MenuWrap className="sm:hidden">
        <SideMenu isAuthorized={true}></SideMenu>
      </MenuWrap>
      <div id="page-wrap">
        <div className="flex">
          <SideMenu isAuthorized={true} className="hidden sm:block"></SideMenu>
          <div className="w-full">
            <SimpleHeader />
            <div className="min-h-[calc(100vh-175px)] bg-gradient-to-br from-white via-[#f7fbf8] to-[#edf4ef] px-6 py-12">
              <div className="mx-auto max-w-7xl space-y-8">
                <div className="rounded-3xl border border-[#dbe7df] bg-white/90 p-8 shadow-xl">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-[#6f8a79]">
                        Account Summary
                      </p>
                      <h1 className="mt-2 text-3xl font-semibold text-[#1f2b23]">
                        {accountLabel} usage and CVE contract
                      </h1>
                      <p className="mt-3 text-sm text-[#6d7b73]">
                        Review your plan access, annual CVE capacity, processed conversion volume, and the usage value accumulated so far.
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <NavLink
                        to="/invite-user"
                        className="rounded-2xl border border-[#cfe0d4] bg-[#f5faf7] px-4 py-3 text-sm font-semibold text-[#1f2b23] transition hover:bg-[#e9f4ed]"
                      >
                        Invite User
                      </NavLink>
                      <NavLink
                        to="/select-plan"
                        className="rounded-2xl border border-[#b6d4bd] bg-[#dff3e3] px-4 py-3 text-sm font-semibold text-[#1f2b23] transition hover:bg-[#d2ebd8]"
                      >
                        Manage Plan
                      </NavLink>
                    </div>
                  </div>

                  {error || summary?.success === false ? (
                    <div className="mt-6 rounded-2xl border border-[#f0c5c5] bg-[#fff5f5] px-5 py-4 text-sm text-[#8a3d3d]">
                      {summary?.error || error?.message || 'We could not load the account summary right now.'}
                    </div>
                  ) : null}

                  {!error && summary?.success !== false ? (
                    <>
                      <div className="mt-8 rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                          <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">
                              {hasAnnualCommitment ? 'Remaining Capacity' : 'Processed CVEs'}
                            </p>
                            <p className="mt-2 text-3xl font-semibold text-[#1f2b23]">
                              {formatNumber(hasAnnualCommitment ? remainingCves : processedCves)} CVEs
                            </p>
                          </div>
                          <div className="text-sm text-[#6d7b73] lg:text-right">
                            <p>{formatNumber(processedCves)} processed</p>
                            <p>{hasAnnualCommitment ? `${formatNumber(committedCves)} annual commitment` : 'No annual commitment'}</p>
                          </div>
                        </div>
                        <div className="mt-5 h-3 overflow-hidden rounded-full bg-[#dbe7df]">
                          <div
                            className="h-full rounded-full bg-[#2f7d4b]"
                            style={{ width: `${usageBarPercent}%` }}
                            aria-label={
                              hasAnnualCommitment
                                ? `${capacityUsedPercent.toFixed(1)}% of CVE capacity used`
                                : `${formatNumber(processedCves)} processed CVEs`
                            }
                          />
                        </div>
                        <div className="mt-2 flex justify-between text-xs uppercase tracking-[0.14em] text-[#6f8a79]">
                          {hasAnnualCommitment ? (
                            <>
                              <span>{capacityUsedPercent.toFixed(1)}% used</span>
                              <span>{Math.max(100 - capacityUsedPercent, 0).toFixed(1)}% remaining</span>
                            </>
                          ) : (
                            <>
                              <span>{formatNumber(processedCves)} processed</span>
                              <span>No annual commitment</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5 xl:row-span-2">
                          <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Contract Tier</p>
                          <p className="mt-3 text-xl font-semibold text-[#1f2b23]">
                            {summary?.contractTierName || summary?.planName || 'No active contract'}
                          </p>
                          <div className="mt-4 space-y-3 text-sm text-[#6d7b73]">
                            <p>Annual commitment: {summary?.committedAnnualCves || 0} CVEs</p>
                            <p>Start date: {formatDateTime(summary?.contractStartDate)}</p>
                            <p>Status: {summary?.subscriptionStatus || 'unknown'}</p>
                          </div>
                        </div>
                        <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Verified / Not Verified</p>
                          <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">
                            {summary?.verifiedCves || 0} / {notVerifiedCves}
                          </p>
                          <p className="mt-2 text-sm text-[#6d7b73]">
                            Total CVEs: {summary?.totalCves || 0}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Processed CVEs</p>
                          <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">
                            {summary?.countedCves || 0}
                          </p>
                          <p className="mt-2 text-sm text-[#6d7b73]">
                            Current window: {summary?.currentPeriodProcessedCves || 0}
                            {summary?.committedAnnualCves ? ` / ${summary.committedAnnualCves}` : ''}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-[#dbe7df] bg-[#f6faf7] p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Usage Value So Far</p>
                          <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">
                            {formatMoney(summary?.usageValueCents, summary?.currency)}
                          </p>
                          <p className="mt-2 text-sm text-[#6d7b73]">
                            Contract rate: {formatRate(summary?.ratePerCveCents, summary?.currency)}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-[#dbe7df] bg-[#fbfdfb] p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Verified CVEs</p>
                          <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">
                            {summary?.verifiedCves || 0}
                          </p>
                          <p className="mt-2 text-sm text-[#6d7b73]">
                            Current window: {summary?.currentPeriodVerifiedCves || 0}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-[#dbe7df] bg-[#fbfdfb] p-5">
                          <p className="text-xs uppercase tracking-[0.18em] text-[#6f8a79]">Annual Contract Value</p>
                          <p className="mt-3 text-3xl font-semibold text-[#1f2b23]">
                            {formatMoney(summary?.annualContractValueCents, summary?.currency)}
                          </p>
                          <p className="mt-2 text-sm text-[#6d7b73]">
                            Annual minimum: {formatMoney(summary?.annualMinimumFeeCents, summary?.currency)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 rounded-2xl border border-[#dbe7df] bg-[#fbfdfb] p-5">
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <p className="text-sm font-semibold text-[#1f2b23]">Cost calculation</p>
                            <p className="mt-1 text-sm text-[#6d7b73]">{summary?.costCalculation || '-'}</p>
                          </div>
                          <p className="text-xs uppercase tracking-[0.16em] text-[#6f8a79]">
                            Organization created {formatDateTime(summary?.organizationCreatedAt)}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 overflow-hidden rounded-3xl border border-[#dbe7df]">
                        <div className="border-b border-[#edf3ee] bg-[#f5faf7] px-5 py-4">
                          <h2 className="text-lg font-semibold text-[#1f2b23]">Users</h2>
                          <p className="mt-1 text-sm text-[#6d7b73]">
                            Click an email address to contact that user directly.
                          </p>
                        </div>
                        <div className="overflow-x-auto bg-white">
                          <table className="min-w-full divide-y divide-[#edf3ee]">
                            <thead className="bg-[#fbfdfb] text-left text-xs uppercase tracking-[0.18em] text-[#6f8a79]">
                              <tr>
                                <th className="px-5 py-4">Name</th>
                                <th className="px-5 py-4">Email</th>
                                <th className="px-5 py-4">State</th>
                                <th className="px-5 py-4">Roles</th>
                                <th className="px-5 py-4">Created</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#edf3ee] text-sm text-[#405147]">
                              {users.map((user) => (
                                <tr key={`${user.email}-${user.createdAt}`} className="align-top">
                                  <td className="px-5 py-4 font-medium text-[#1f2b23]">
                                    {user.fullName || '-'}
                                  </td>
                                  <td className="px-5 py-4">
                                    <a
                                      href={`mailto:${user.email}`}
                                      className="font-medium text-[#255a38] underline decoration-[#a7c5b0] underline-offset-4"
                                    >
                                      {user.email}
                                    </a>
                                  </td>
                                  <td className="px-5 py-4">{user.userState || '-'}</td>
                                  <td className="px-5 py-4">
                                    {user.userRoles?.length ? user.userRoles.join(', ') : '-'}
                                  </td>
                                  <td className="px-5 py-4">{formatDateTime(user.createdAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSummary
